/**
 * bindingUtils — Step 17.3 / 17.5 / 18.1
 *
 * Pure utilities for Conductor runtime dot-path parsing and resolution.
 * Used by both the Preview runtime (usePreviewRuntime) and the
 * Actions & Bindings Inspector (ActionInspector) for diagnostics.
 *
 * ── Supported source grammar ──────────────────────────────────────────────────
 *
 *   actions.<actionId>.response
 *   actions.<actionId>.response.body
 *   actions.<actionId>.response.body.<field>
 *   actions.<actionId>.response.body.<nested>.<field>
 *
 * This is a Conductor runtime dot path, not JSONPath.
 *
 * ── Supported target grammar ──────────────────────────────────────────────────
 *
 *   components.<componentName>.value    → JsonViewer, Label, TextArea, Checkbox, RadioGroup, StatusPanel
 *   components.<componentName>.options  → Dropdown only
 *   components.<componentName>.rows     → Table only (Step 17.5)
 *   variables.<variableName>            → runtime variable (Step 18.1)
 *
 * ── Supported triggers ───────────────────────────────────────────────────────
 *
 *   onSuccess   — canonical (Step 17.1+)
 *   onClick     — legacy Step 15 compatibility
 *
 * ── Not supported ─────────────────────────────────────────────────────────────
 *
 *   Wildcards, filters, array transforms, JSONPath, bracket notation,
 *   keys containing literal dots, component-change bindings.
 */

import type { ProxyResponse } from '../../api/proxyApi';
import type { DropdownOption, TableRow } from '../../types/project';

// ── Action runtime state ──────────────────────────────────────────────────────

/**
 * Ephemeral runtime state for a single REST action.
 * Stored in Preview runtime; never written to the canonical project document.
 */
export type ActionRuntimeState = {
  /** The full ProxyResponse envelope from the most recent execution. */
  response?: ProxyResponse;
  /** True while the action is in-flight. */
  loading?: boolean;
  /** Human-readable error from the last failed execution. */
  error?: string;
};

/** Map keyed by action ID. */
export type ActionRuntimeStateMap = Record<string, ActionRuntimeState>;

// ── Target capability model ───────────────────────────────────────────────────

/**
 * Component types that may receive a runtime `.value` update via response bindings.
 * Scoped to the `.value` property only.
 * Dropdown is intentionally excluded — it uses `.options` instead.
 */
export const SUPPORTED_TARGET_TYPES = new Set([
  'JsonViewer',
  'Label',
  'TextArea',
  'Checkbox',
  'RadioGroup',
  'StatusPanel',
]);

/**
 * Property-aware target capability check.
 *
 * Returns true when the given component type supports the given target property:
 *   .value   → JsonViewer, Label, TextArea, Checkbox, RadioGroup, StatusPanel
 *   .options → Dropdown
 *   .rows    → Table
 *
 * All other combinations return false.
 */
export function isTargetPropertySupported(componentType: string, property: string): boolean {
  if (property === 'value') return SUPPORTED_TARGET_TYPES.has(componentType);
  if (property === 'options') return componentType === 'Dropdown';
  if (property === 'rows') return componentType === 'Table';
  return false;
}

// ── Trigger constants ─────────────────────────────────────────────────────────

/** Canonical trigger for action-response bindings (Step 17.1+). */
export const TRIGGER_ON_SUCCESS = 'onSuccess';

/** Legacy trigger accepted for backward compatibility with Step 15 examples. */
export const TRIGGER_LEGACY_ON_CLICK = 'onClick';

// ── Source path parsing ───────────────────────────────────────────────────────

/**
 * Parsed representation of an action-response source path.
 * Null when the expression is not an action-response path.
 */
export type ParsedActionSource = {
  /** The action ID extracted from the path. */
  actionId: string;
  /**
   * Remaining segments after "response", split on ".".
   * Empty array means the path is exactly "actions.<id>.response".
   * ["body"] means ".response.body".
   * ["body", "origin"] means ".response.body.origin".
   */
  tail: string[];
};

/**
 * Parses an action-response source expression.
 *
 * Valid forms:
 *   actions.<id>.response
 *   actions.<id>.response.body
 *   actions.<id>.response.body.<field...>
 *
 * Returns null for anything else (component paths, variables, malformed).
 */
export function parseActionSourcePath(expr: string): ParsedActionSource | null {
  // Must start with "actions."
  if (!expr.startsWith('actions.')) return null;

  // Split on "." — expect at minimum ["actions", "<id>", "response"]
  const parts = expr.split('.');

  if (parts.length < 3) return null;
  if (parts[0] !== 'actions') return null;

  const actionId = parts[1];
  if (!actionId) return null;

  if (parts[2] !== 'response') return null;

  // Tail is everything after "response"
  const tail = parts.slice(3);

  return { actionId, tail };
}

// ── Source value resolution ───────────────────────────────────────────────────

/** Sentinel distinguishing "field exists with value undefined" (unusual) from
 *  "path traversal failed" (field genuinely absent). We use a typed result. */
export type SourceResolveResult =
  | { found: true; value: unknown }
  | { found: false; reason: string };

/**
 * Resolves a parsed action source against the runtime action state.
 *
 * Traversal rules:
 *   - Falsy values (false, 0, "", null) are valid resolved values.
 *   - Missing fields return { found: false }.
 *   - Traversal into a non-object/non-null mid-path returns { found: false }.
 */
export function resolveActionSource(
  parsed: ParsedActionSource,
  actionState: ActionRuntimeStateMap,
): SourceResolveResult {
  const state = actionState[parsed.actionId];
  if (!state) {
    return {
      found: false,
      reason: `Action "${parsed.actionId}" has no runtime state. Has the action been executed?`,
    };
  }
  if (state.response === undefined) {
    return {
      found: false,
      reason: `Action "${parsed.actionId}" has no response yet.`,
    };
  }

  // Path is exactly "actions.<id>.response" — return full envelope
  if (parsed.tail.length === 0) {
    return { found: true, value: state.response };
  }

  // Next segment must be "body"
  if (parsed.tail[0] !== 'body') {
    return {
      found: false,
      reason:
        `Unsupported source path segment "${parsed.tail[0]}" after "response". ` +
        `Supported forms: response, response.body, response.body.<field…>`,
    };
  }

  // Path is "actions.<id>.response.body"
  if (parsed.tail.length === 1) {
    return { found: true, value: state.response.body };
  }

  // Traverse remaining segments into body
  const fieldPath = parsed.tail.slice(1);
  let current: unknown = state.response.body;

  for (let i = 0; i < fieldPath.length; i++) {
    const segment = fieldPath[i];

    if (current === null || current === undefined) {
      const traversed = ['response', 'body', ...fieldPath.slice(0, i)].join('.');
      return {
        found: false,
        reason:
          `Cannot read "${segment}" — value at "${traversed}" is ` +
          `${current === null ? 'null' : 'undefined'}.`,
      };
    }

    if (typeof current !== 'object' || Array.isArray(current)) {
      const traversed = ['response', 'body', ...fieldPath.slice(0, i)].join('.');
      return {
        found: false,
        reason:
          `Cannot traverse into "${traversed}" — it is ` +
          `${Array.isArray(current) ? 'an array' : `a ${typeof current}`}, not an object.`,
      };
    }

    const obj = current as Record<string, unknown>;
    if (!Object.prototype.hasOwnProperty.call(obj, segment)) {
      const traversed = ['response', 'body', ...fieldPath.slice(0, i + 1)].join('.');
      return {
        found: false,
        reason: `Field "${traversed}" does not exist in the action response body.`,
      };
    }

    current = obj[segment];
  }

  return { found: true, value: current };
}

// ── Target path parsing ───────────────────────────────────────────────────────

/**
 * Parsed representation of a component target path.
 */
export type ParsedComponentTarget = {
  componentName: string;
  property: string;
};

/**
 * Parses a component target expression.
 *
 * Valid forms:
 *   components.<name>.value
 *   components.<name>.options
 *   components.<name>.rows
 *
 * Returns null for anything else (variables, malformed, missing segments).
 */
export function parseComponentTargetPath(expr: string): ParsedComponentTarget | null {
  if (!expr.startsWith('components.')) return null;

  const parts = expr.split('.');
  // Expect exactly ["components", "<name>", "<property>"]
  if (parts.length !== 3) return null;
  if (!parts[1] || !parts[2]) return null;

  return { componentName: parts[1], property: parts[2] };
}

// ── Variable target path parsing (Step 18.1) ──────────────────────────────────

/**
 * Parsed representation of a variable target path.
 */
export type ParsedVariableTarget = {
  variableName: string;
};

/**
 * Parses a variable target expression.
 *
 * Valid form:
 *   variables.<name>
 *
 * Returns null for anything else (components, malformed, missing segments).
 * The variable name must be a non-empty string with no embedded dots.
 */
export function parseVariableTargetPath(expr: string): ParsedVariableTarget | null {
  if (!expr.startsWith('variables.')) return null;

  // Expect exactly "variables.<name>" — one dot, non-empty name, no further dots
  const rest = expr.slice('variables.'.length);
  if (!rest || rest.includes('.')) return null;

  return { variableName: rest };
}

// ── Trigger classification ────────────────────────────────────────────────────

export type TriggerClassification =
  | 'onSuccess'          // canonical
  | 'onClick-legacy'     // Step 15 compat
  | 'unsupported';       // everything else

/**
 * Classifies a binding trigger in the context of action-response bindings.
 * Returns 'onSuccess', 'onClick-legacy', or 'unsupported'.
 */
export function classifyTrigger(trigger: string | undefined): TriggerClassification {
  const t = trigger ?? 'onChange';
  if (t === TRIGGER_ON_SUCCESS) return 'onSuccess';
  if (t === TRIGGER_LEGACY_ON_CLICK) return 'onClick-legacy';
  return 'unsupported';
}

// ── Dropdown option normalisation ────────────────────────────────────────────

/**
 * Result of normalising a raw response-array value into DropdownOption[].
 */
export type NormalizeOptionsResult =
  | { ok: true; options: DropdownOption[]; warnings: string[] }
  | { ok: false; reason: string };

/**
 * Normalises a raw value from an action response into an array of DropdownOption.
 *
 * Accepted input shapes:
 *   Shape A — array of strings:  ["host1", "host2"]
 *   Shape B — array of {label: string, value: string} objects
 *             (extra fields on objects are ignored)
 *
 * Rejected shapes (returns ok:false):
 *   - Not an array
 *   - Mixed array (strings and objects together)
 *   - Array containing numbers, booleans, null
 *   - Array of objects missing string `label`
 *   - Array of objects missing string `value`
 *   - Array of nested option structures (arrays inside)
 *
 * Empty array is valid — produces ok:true with options:[].
 *
 * Warnings (ok:true, warnings non-empty):
 *   - Duplicate option values
 *   - Empty label string
 *   - Empty value string
 *
 * Does not auto-convert numbers or booleans to strings.
 * Does not guess fields like name, id, title, key, displayName.
 */
export function normalizeDropdownOptions(raw: unknown): NormalizeOptionsResult {
  if (!Array.isArray(raw)) {
    return {
      ok: false,
      reason:
        `Expected an array of strings or label/value objects, but got ` +
        `${raw === null ? 'null' : typeof raw}. ` +
        `Source must resolve to an array before options can be applied.`,
    };
  }

  if (raw.length === 0) {
    return { ok: true, options: [], warnings: [] };
  }

  // Detect the shape from the first element
  const first = raw[0];
  const isStringArray = typeof first === 'string';
  const isObjectArray = typeof first === 'object' && first !== null && !Array.isArray(first);

  if (!isStringArray && !isObjectArray) {
    return {
      ok: false,
      reason:
        `Array element at index 0 has unsupported type ` +
        `${first === null ? 'null' : Array.isArray(first) ? 'array' : typeof first}. ` +
        `Only arrays of strings or label/value objects are supported.`,
    };
  }

  // Validate each element for consistency and correctness
  for (let i = 0; i < raw.length; i++) {
    const el = raw[i];

    if (isStringArray) {
      // All elements must be strings
      if (typeof el !== 'string') {
        return {
          ok: false,
          reason:
            `Mixed array: element at index 0 is a string but element at index ${i} is ` +
            `${el === null ? 'null' : Array.isArray(el) ? 'an array' : `a ${typeof el}`}. ` +
            `Do not partially apply valid elements from a mixed array.`,
        };
      }
    } else {
      // All elements must be plain objects with string label and string value
      if (typeof el !== 'object' || el === null || Array.isArray(el)) {
        return {
          ok: false,
          reason:
            `Mixed array: element at index 0 is an object but element at index ${i} is ` +
            `${el === null ? 'null' : Array.isArray(el) ? 'an array' : `a ${typeof el}`}. ` +
            `Do not partially apply valid elements from a mixed array.`,
        };
      }
      const obj = el as Record<string, unknown>;
      if (typeof obj['label'] !== 'string') {
        return {
          ok: false,
          reason:
            `Object at index ${i} is missing a string "label" field ` +
            `(found: ${obj['label'] === undefined ? 'undefined' : `${typeof obj['label']} "${obj['label']}"`}). ` +
            `Expected: { "label": string, "value": string }.`,
        };
      }
      if (typeof obj['value'] !== 'string') {
        return {
          ok: false,
          reason:
            `Object at index ${i} is missing a string "value" field ` +
            `(found: ${obj['value'] === undefined ? 'undefined' : `${typeof obj['value']} "${obj['value']}"`}). ` +
            `Expected: { "label": string, "value": string }.`,
        };
      }
    }
  }

  // All elements valid — build normalized options (new objects, no response references retained)
  const options: DropdownOption[] = isStringArray
    ? (raw as string[]).map((s) => ({ label: s, value: s }))
    : (raw as Array<Record<string, unknown>>).map((obj) => ({
        label: obj['label'] as string,
        value: obj['value'] as string,
      }));

  // Collect warnings for empty labels/values and duplicate values
  const warnings: string[] = [];
  const seenValues = new Set<string>();
  const dupValues = new Set<string>();

  for (let i = 0; i < options.length; i++) {
    const opt = options[i];
    if (!opt.label) {
      warnings.push(`Option at index ${i} has an empty label.`);
    }
    if (!opt.value) {
      warnings.push(`Option at index ${i} has an empty value.`);
    }
    if (opt.value) {
      if (seenValues.has(opt.value)) dupValues.add(opt.value);
      seenValues.add(opt.value);
    }
  }

  if (dupValues.size > 0) {
    warnings.push(
      `Duplicate option values detected: ${[...dupValues].map((v) => `"${v}"`).join(', ')}. ` +
      `Runtime selection may be ambiguous.`,
    );
  }

  return { ok: true, options, warnings };
}

/**
 * Reconciles the current Dropdown selection after runtime options are replaced.
 *
 * Effective current value (in priority order):
 *   1. runtimeValue if defined
 *   2. configuredValue if non-empty
 *   3. ""
 *
 * If the effective value exists in the new options, it is preserved.
 * If it does not exist, returns "".
 * The first option is never auto-selected.
 * An empty options array always clears the selection.
 */
export function reconcileDropdownSelection(
  runtimeValue: unknown,
  configuredValue: string,
  newOptions: DropdownOption[],
): string {
  const effective =
    runtimeValue !== undefined
      ? String(runtimeValue)
      : configuredValue !== ''
        ? configuredValue
        : '';

  if (effective === '') return '';
  if (newOptions.some((o) => o.value === effective)) return effective;
  return '';
}

// ── Label value serialisation ─────────────────────────────────────────────────

/**
 * Converts a runtime binding value to a display string for Label components.
 *
 * Rules:
 *   string              → render directly
 *   number | boolean    → String(value)
 *   null                → "null"
 *   object | array      → compact JSON.stringify
 *   serialisation fails → "[object: serialisation error]"
 */
export function labelDisplayValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value === null) return 'null';
  try {
    return JSON.stringify(value);
  } catch {
    return '[object: serialisation error]';
  }
}

// ── Table row normalization ───────────────────────────────────────────────────

/**
 * Result of normalising a raw response-array value into TableRow[].
 */
export type NormalizeTableRowsResult =
  | { ok: true; rows: TableRow[] }
  | { ok: false; reason: string };

/**
 * Normalises a raw value from an action response into an array of TableRow.
 *
 * Accepted input:
 *   Array<Record<string, unknown>>
 *   - every element must be a non-null, non-array object
 *   - empty array is valid
 *   - elements may contain strings, numbers, booleans, null, nested objects/arrays
 *
 * Rejected shapes (returns ok:false):
 *   - not an array
 *   - array of strings / numbers / booleans
 *   - array containing null
 *   - array containing nested arrays
 *   - mixed object/non-object array
 *
 * On acceptance:
 *   - row order is preserved
 *   - shallow copies of each row object are created (no retained response references)
 *
 * Does not partially apply valid rows from an invalid array.
 */
export function normalizeTableRows(raw: unknown): NormalizeTableRowsResult {
  if (!Array.isArray(raw)) {
    return {
      ok: false,
      reason:
        `Expected an array of objects, but got ` +
        `${raw === null ? 'null' : typeof raw}. ` +
        `Source must resolve to an array of objects before rows can be applied.`,
    };
  }

  if (raw.length === 0) {
    return { ok: true, rows: [] };
  }

  for (let i = 0; i < raw.length; i++) {
    const el = raw[i];
    if (el === null) {
      return {
        ok: false,
        reason: `Array element at index ${i} is null. Table rows must be non-null objects.`,
      };
    }
    if (Array.isArray(el)) {
      return {
        ok: false,
        reason: `Array element at index ${i} is an array. Table rows must be non-array objects.`,
      };
    }
    if (typeof el !== 'object') {
      return {
        ok: false,
        reason:
          `Array element at index ${i} has unsupported type ${typeof el}. ` +
          `Table rows must be plain objects.`,
      };
    }
  }

  // All elements valid — create shallow copies (no retained response references)
  const rows: TableRow[] = (raw as Record<string, unknown>[]).map((el) => ({ ...el }));
  return { ok: true, rows };
}
